import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  X, School, GraduationCap, BookOpen, Building2, MapPin, Phone, Mail,
  Calendar, Users, Award, Globe, Facebook, Instagram, Twitter, Trophy,
  Clock, FileText, User, CheckCircle, Download
} from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { admissionsService } from '../../firebase/admissions';
import toast from 'react-hot-toast';

const ComparisonView = ({ institutions, onClose }) => {
  const [institutionsData, setInstitutionsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAllData();
  }, [institutions]);



  const fetchRealStudentTeacherData = async (institutionId) => {
    try {
      console.log('=== Fetching data for institution:', institutionId, '===');
      
      // Try multiple collection names and field names
      const collectionNames = ['users', 'userProfiles'];
      const coachingFields = ['coachingId', 'institutionId', 'coaching_id'];
      const studentRoles = ['student', 'Student', 'STUDENT', 'learner', 'user'];
      const teacherRoles = ['teacher', 'Teacher', 'TEACHER', 'instructor', 'faculty'];
      
      let realStudentsCount = 0;
      let realTeachersCount = 0;
      let totalExperience = 0;
      let foundCollection = null;
      let foundField = null;
      const studentIds = new Set(); // Track unique student IDs
      
      // First, find which collection and field combination works for teachers (since they're loading)
      for (const collectionName of collectionNames) {
        for (const coachingField of coachingFields) {
          try {
            const teachersQuery = query(
              collection(db, collectionName),
              where(coachingField, '==', institutionId),
              where('role', '==', 'teacher')
            );
            const teachersSnapshot = await getDocs(teachersQuery);
            
            if (teachersSnapshot.size > 0) {
              realTeachersCount = teachersSnapshot.size;
              foundCollection = collectionName;
              foundField = coachingField;
              console.log(`✓ Found ${realTeachersCount} teachers in ${collectionName} using ${coachingField}`);
              
              // Calculate total faculty experience
              teachersSnapshot.forEach((doc) => {
                const teacherData = doc.data();
                const experience = teacherData.teacherProfile?.experience || 
                                 teacherData.experience || 
                                 teacherData.yearsOfExperience ||
                                 teacherData.years_of_experience;
                
                if (experience) {
                  const years = parseInt(experience.toString().match(/\d+/)?.[0] || 0);
                  totalExperience += years;
                }
              });
              console.log(`✓ Total faculty experience: ${totalExperience} years`);
              break;
            }
          } catch (queryError) {
            console.log(`✗ Teacher query failed for ${collectionName}.${coachingField}:`, queryError.message);
          }
        }
        
        if (foundCollection && foundField) break;
      }
      
      // Now try to find students using multiple approaches
      if (foundCollection && foundField) {
        console.log(`Searching for students in ${foundCollection} using ${foundField}...`);
        
        // Approach 1: Try different student role values
        for (const studentRole of studentRoles) {
          try {
            const studentsQuery = query(
              collection(db, foundCollection),
              where(foundField, '==', institutionId),
              where('role', '==', studentRole)
            );
            const studentsSnapshot = await getDocs(studentsQuery);
            
            if (studentsSnapshot.size > 0) {
              studentsSnapshot.forEach(doc => studentIds.add(doc.id));
              console.log(`✓ Found ${studentsSnapshot.size} students with role='${studentRole}'`);
            } else {
              console.log(`✗ No students found with role='${studentRole}'`);
            }
          } catch (queryError) {
            console.log(`✗ Student query failed for role='${studentRole}':`, queryError.message);
          }
        }
        
        // Approach 2: Check batch enrollments
        console.log('Checking batch enrollments...');
        try {
          // First get all batches for this coaching
          const batchesQuery = query(
            collection(db, 'batches'),
            where('coachingId', '==', institutionId)
          );
          const batchesSnapshot = await getDocs(batchesQuery);
          console.log(`Found ${batchesSnapshot.size} batches for this institution`);
          
          // For each batch, get enrollments
          for (const batchDoc of batchesSnapshot.docs) {
            try {
              const enrollmentsQuery = query(
                collection(db, 'batchEnrollments'),
                where('batchId', '==', batchDoc.id)
              );
              const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
              
              enrollmentsSnapshot.forEach(enrollDoc => {
                const enrollData = enrollDoc.data();
                if (enrollData.userId || enrollData.studentId) {
                  studentIds.add(enrollData.userId || enrollData.studentId);
                }
              });
              
              console.log(`Batch ${batchDoc.id}: ${enrollmentsSnapshot.size} enrollments`);
            } catch (enrollError) {
              console.log(`Error fetching enrollments for batch ${batchDoc.id}:`, enrollError.message);
            }
          }
        } catch (batchError) {
          console.log(`Error fetching batches:`, batchError.message);
        }
        
        // Approach 3: Get all users and count non-teachers
        console.log('Fetching all users for this institution...');
        try {
          const allUsersQuery = query(
            collection(db, foundCollection),
            where(foundField, '==', institutionId)
          );
          const allUsersSnapshot = await getDocs(allUsersQuery);
          
          console.log(`Found ${allUsersSnapshot.size} total users`);
          
          // Log all unique role values and user details
          const roleValues = new Set();
          const userDetails = [];
          
          allUsersSnapshot.forEach((doc) => {
            const userData = doc.data();
            const role = userData.role?.toLowerCase();
            
            if (userData.role) {
              roleValues.add(userData.role);
            }
            
            userDetails.push({
              id: doc.id,
              role: userData.role,
              email: userData.email,
              name: userData.displayName || userData.name
            });
            
            // If not a teacher, add to student IDs
            if (!teacherRoles.some(tr => tr.toLowerCase() === role)) {
              studentIds.add(doc.id);
            }
          });
          
          console.log('Unique role values found:', Array.from(roleValues));
          console.log('User details:', userDetails);
        } catch (fallbackError) {
          console.log(`✗ Fallback query failed:`, fallbackError.message);
        }
      }

      realStudentsCount = studentIds.size;
      console.log(`=== Final counts - Students: ${realStudentsCount}, Teachers: ${realTeachersCount}, Experience: ${totalExperience} ===`);
      
      return {
        realStudentsCount,
        realTeachersCount,
        totalFacultyExperience: totalExperience
      };
    } catch (error) {
      console.error('Error fetching real data:', error);
      return {
        realStudentsCount: 0,
        realTeachersCount: 0,
        totalFacultyExperience: 0
      };
    }
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      const dataPromises = institutions.map(async (inst) => {
        const [
          customization,
          curriculum,
          materials,
          faculty,
          feeStructure,
          results,
          realData
        ] = await Promise.all([
          admissionsService.getInstitutionCustomization(inst.id),
          admissionsService.getInstitutionCurriculum(inst.id),
          admissionsService.getInstitutionMaterials(inst.id),
          admissionsService.getInstitutionFaculty(inst.id),
          admissionsService.getInstitutionFeeStructure(inst.id),
          admissionsService.getInstitutionResults(inst.id),
          fetchRealStudentTeacherData(inst.id)
        ]);

        return {
          ...inst,
          customization: customization.success ? customization.customization : null,
          curriculum: curriculum.success ? curriculum.curriculum : [],
          materials: materials.success ? materials.materials : [],
          faculty: faculty.success ? faculty.faculty : [],
          feeStructure: feeStructure.success ? feeStructure.feeStructure : null,
          results: results.success ? results.results : [],
          realStudentsCount: realData.realStudentsCount,
          realTeachersCount: realData.realTeachersCount,
          totalFacultyExperience: realData.totalFacultyExperience
        };
      });

      const data = await Promise.all(dataPromises);
      setInstitutionsData(data);
    } catch (error) {
      toast.error('Error loading comparison data');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'school': return <School className="w-5 h-5" />;
      case 'college': return <GraduationCap className="w-5 h-5" />;
      case 'coaching': return <BookOpen className="w-5 h-5" />;
      default: return <Building2 className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'school': return 'bg-blue-100 text-blue-800';
      case 'college': return 'bg-purple-100 text-purple-800';
      case 'coaching': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading comparison data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-auto">
      {/* Header */}
      <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg z-30">
        <div className="px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Institution Comparison</h1>
            <p className="text-blue-100 text-sm">Comparing {institutionsData.length} institutions</p>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 rounded-lg p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>



      {/* Comparison Table */}
      <div className="p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-6 h-6 text-blue-600" />
          Detailed Comparison
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-gray-100 border border-gray-300 p-4 text-left font-bold text-gray-900 min-w-[250px] w-[250px] z-10">
                  Parameter
                </th>
                {institutionsData.map((inst, idx) => (
                  <th key={inst.id} className="border border-gray-300 p-4 min-w-[280px] bg-gray-50">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg overflow-hidden">
                        {inst.profilePicture || inst.logo ? (
                          <img
                            src={inst.profilePicture || inst.logo}
                            alt={inst.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {getTypeIcon(inst.type)}
                          </div>
                        )}
                      </div>
                      <div className="text-center">
                        <h3 className="font-bold text-gray-900 text-sm mb-1">{inst.name}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(inst.type)} inline-flex items-center gap-1`}>
                          {getTypeIcon(inst.type)}
                          {inst.type}
                        </span>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Derived Metrics Section */}
              <tr className="bg-blue-50">
                <td colSpan={institutionsData.length + 1} className="border border-gray-300 p-3">
                  <h3 className="font-bold text-blue-900 text-lg flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Derived Metrics & Analysis
                  </h3>
                </td>
              </tr>

              <ComparisonRow
                label="Years of Experience"
                icon={<Calendar className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  if (!inst.established) return 'N/A';
                  const years = new Date().getFullYear() - parseInt(inst.established);
                  return `${years} years`;
                })}
                highlightBest={true}
                bestIndex={institutionsData.map(inst => {
                  if (!inst.established) return 0;
                  return new Date().getFullYear() - parseInt(inst.established);
                }).indexOf(Math.max(...institutionsData.map(inst => {
                  if (!inst.established) return 0;
                  return new Date().getFullYear() - parseInt(inst.established);
                })))}
              />

              <ComparisonRow
                label="Real Students Count"
                icon={<Users className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const count = inst.realStudentsCount || 0;
                  return count > 0 ? `${count} students` : 'N/A';
                })}
                highlightBest={true}
                bestIndex={institutionsData.map(inst => inst.realStudentsCount || 0)
                  .indexOf(Math.max(...institutionsData.map(inst => inst.realStudentsCount || 0)))}
              />

              <ComparisonRow
                label="Real Teachers Count"
                icon={<Users className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const count = inst.realTeachersCount || 0;
                  return count > 0 ? `${count} teachers` : 'N/A';
                })}
                highlightBest={true}
                bestIndex={institutionsData.map(inst => inst.realTeachersCount || 0)
                  .indexOf(Math.max(...institutionsData.map(inst => inst.realTeachersCount || 0)))}
              />

              <ComparisonRow
                label="Student-Teacher Ratio"
                icon={<Users className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const students = inst.realStudentsCount || 0;
                  const teachers = inst.realTeachersCount || 0;
                  if (students === 0 || teachers === 0) return 'N/A';
                  const ratio = (students / teachers).toFixed(1);
                  return `${ratio} students per teacher`;
                })}
              />

              <ComparisonRow
                label="Total Faculty Experience"
                icon={<Award className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const experience = inst.totalFacultyExperience || 0;
                  return experience > 0 ? `${experience} years` : 'N/A';
                })}
                highlightBest={true}
                bestIndex={institutionsData.map(inst => inst.totalFacultyExperience || 0)
                  .indexOf(Math.max(...institutionsData.map(inst => inst.totalFacultyExperience || 0)))}
              />

              <ComparisonRow
                label="Avg. Faculty Experience"
                icon={<Award className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const totalExp = inst.totalFacultyExperience || 0;
                  const teacherCount = inst.realTeachersCount || 0;
                  if (totalExp === 0 || teacherCount === 0) return 'N/A';
                  const avg = (totalExp / teacherCount).toFixed(1);
                  return `${avg} years`;
                })}
                highlightBest={true}
                bestIndex={institutionsData.map(inst => {
                  const totalExp = inst.totalFacultyExperience || 0;
                  const teacherCount = inst.realTeachersCount || 0;
                  if (totalExp === 0 || teacherCount === 0) return 0;
                  return totalExp / teacherCount;
                }).indexOf(Math.max(...institutionsData.map(inst => {
                  const totalExp = inst.totalFacultyExperience || 0;
                  const teacherCount = inst.realTeachersCount || 0;
                  if (totalExp === 0 || teacherCount === 0) return 0;
                  return totalExp / teacherCount;
                })))}
              />

              <ComparisonRow
                label="Total Qualified Students"
                icon={<Trophy className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const total = inst.results?.reduce((sum, r) => sum + (parseInt(r.qualified) || 0), 0) || 0;
                  return total > 0 ? `${total} students` : 'N/A';
                })}
                highlightBest={true}
                bestIndex={institutionsData.map(inst => 
                  inst.results?.reduce((sum, r) => sum + (parseInt(r.qualified) || 0), 0) || 0
                ).indexOf(Math.max(...institutionsData.map(inst => 
                  inst.results?.reduce((sum, r) => sum + (parseInt(r.qualified) || 0), 0) || 0
                )))}
              />

              <ComparisonRow
                label="Success Rate"
                icon={<Trophy className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const totalStudents = inst.results?.reduce((sum, r) => sum + (parseInt(r.totalStudents) || 0), 0) || 0;
                  const qualified = inst.results?.reduce((sum, r) => sum + (parseInt(r.qualified) || 0), 0) || 0;
                  if (totalStudents === 0) return 'N/A';
                  const rate = Math.round((qualified / totalStudents) * 100);
                  return `${rate}%`;
                })}
                highlightBest={true}
                bestIndex={institutionsData.map(inst => {
                  const totalStudents = inst.results?.reduce((sum, r) => sum + (parseInt(r.totalStudents) || 0), 0) || 0;
                  const qualified = inst.results?.reduce((sum, r) => sum + (parseInt(r.qualified) || 0), 0) || 0;
                  if (totalStudents === 0) return 0;
                  return (qualified / totalStudents) * 100;
                }).indexOf(Math.max(...institutionsData.map(inst => {
                  const totalStudents = inst.results?.reduce((sum, r) => sum + (parseInt(r.totalStudents) || 0), 0) || 0;
                  const qualified = inst.results?.reduce((sum, r) => sum + (parseInt(r.qualified) || 0), 0) || 0;
                  if (totalStudents === 0) return 0;
                  return (qualified / totalStudents) * 100;
                })))}
              />

              <ComparisonRow
                label="Course Variety"
                icon={<BookOpen className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const count = (inst.curriculum?.length || 0) + ((inst.customization?.programs || inst.programs)?.length || 0);
                  return count > 0 ? `${count} offerings` : 'N/A';
                })}
                highlightBest={true}
                bestIndex={institutionsData.map(inst => 
                  (inst.curriculum?.length || 0) + ((inst.customization?.programs || inst.programs)?.length || 0)
                ).indexOf(Math.max(...institutionsData.map(inst => 
                  (inst.curriculum?.length || 0) + ((inst.customization?.programs || inst.programs)?.length || 0)
                )))}
              />



              {/* Basic Information Section */}
              <tr className="bg-gray-100">
                <td colSpan={institutionsData.length + 1} className="border border-gray-300 p-3">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Basic Information
                  </h3>
                </td>
              </tr>

              <ComparisonRow
                label="Description"
                icon={<FileText className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const displayData = inst.customization || inst;
                  return displayData.description || inst.description || 'N/A';
                })}
              />

              <ComparisonRow
                label="Location"
                icon={<MapPin className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  if (!inst.address) return 'N/A';
                  return `${inst.address.city || ''}${inst.address.state ? `, ${inst.address.state}` : ''}`;
                })}
              />

              <ComparisonRow
                label="Established"
                icon={<Calendar className="w-4 h-4" />}
                data={institutionsData.map(inst => inst.established || 'N/A')}
              />

              <ComparisonRow
                label="Total Students"
                icon={<Users className="w-4 h-4" />}
                data={institutionsData.map(inst => inst.students || 'N/A')}
              />

              <ComparisonRow
                label="Application Fee"
                icon={<span className="font-bold">₹</span>}
                data={institutionsData.map(inst => inst.applicationFee ? `₹${inst.applicationFee}` : 'N/A')}
              />

              {/* Contact Information Section */}
              <tr className="bg-gray-100">
                <td colSpan={institutionsData.length + 1} className="border border-gray-300 p-3">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Contact Information
                  </h3>
                </td>
              </tr>

              <ComparisonRow
                label="Phone"
                icon={<Phone className="w-4 h-4" />}
                data={institutionsData.map(inst => inst.contact?.phone || 'N/A')}
              />

              <ComparisonRow
                label="Email"
                icon={<Mail className="w-4 h-4" />}
                data={institutionsData.map(inst => inst.contact?.email || 'N/A')}
              />

              <ComparisonRow
                label="Website"
                icon={<Globe className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const website = inst.contact?.website;
                  return website ? (
                    <a href={website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                      Visit Website
                    </a>
                  ) : 'N/A';
                })}
              />

              {/* Features & Programs Section */}
              <tr className="bg-gray-100">
                <td colSpan={institutionsData.length + 1} className="border border-gray-300 p-3">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Features & Programs
                  </h3>
                </td>
              </tr>

              <ComparisonRow
                label="Key Features"
                icon={<Award className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const displayData = inst.customization || inst;
                  if (!displayData.features || displayData.features.length === 0) return 'N/A';
                  return (
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {displayData.features.slice(0, 5).map((feature, idx) => (
                        <li key={idx} className="text-gray-700">{feature}</li>
                      ))}
                      {displayData.features.length > 5 && (
                        <li className="text-gray-500 italic">+{displayData.features.length - 5} more</li>
                      )}
                    </ul>
                  );
                })}
              />

              {/* Programs */}
              <ComparisonRow
                label="Programs Offered"
                icon={<BookOpen className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const displayData = inst.customization || inst;
                  if (!displayData.programs || displayData.programs.length === 0) return 'N/A';
                  return (
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {displayData.programs.slice(0, 5).map((program, idx) => (
                        <li key={idx} className="text-gray-700">{program}</li>
                      ))}
                      {displayData.programs.length > 5 && (
                        <li className="text-gray-500 italic">+{displayData.programs.length - 5} more</li>
                      )}
                    </ul>
                  );
                })}
              />

              {/* Courses & Curriculum */}
              <ComparisonRow
                label="Courses & Programs"
                icon={<BookOpen className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  if (!inst.curriculum || inst.curriculum.length === 0) return 'N/A';
                  return (
                    <div className="space-y-2">
                      {inst.curriculum.slice(0, 3).map((course, idx) => (
                        <div key={idx} className="text-sm bg-gray-50 p-2 rounded">
                          <p className="font-semibold text-gray-900">{course.title}</p>
                          {course.duration && <p className="text-xs text-gray-600">Duration: {course.duration}</p>}
                          {course.fees && <p className="text-xs text-gray-600">Fees: {course.fees}</p>}
                        </div>
                      ))}
                      {inst.curriculum.length > 3 && (
                        <p className="text-xs text-gray-500 italic">+{inst.curriculum.length - 3} more courses</p>
                      )}
                    </div>
                  );
                })}
              />

              {/* Academic Resources Section */}
              <tr className="bg-gray-100">
                <td colSpan={institutionsData.length + 1} className="border border-gray-300 p-3">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" />
                    Academic Resources
                  </h3>
                </td>
              </tr>

              <ComparisonRow
                label="Faculty Members"
                icon={<Users className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  if (!inst.faculty || inst.faculty.length === 0) return 'N/A';
                  return `${inst.faculty.length} faculty members`;
                })}
              />

              {/* Materials */}
              <ComparisonRow
                label="Brochures & Materials"
                icon={<FileText className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  if (!inst.materials || inst.materials.length === 0) return 'N/A';
                  return `${inst.materials.length} materials available`;
                })}
              />

              {/* Financial Information Section */}
              <tr className="bg-gray-100">
                <td colSpan={institutionsData.length + 1} className="border border-gray-300 p-3">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <span className="text-xl">₹</span>
                    Financial Information
                  </h3>
                </td>
              </tr>

              <ComparisonRow
                label="Fee Structure"
                icon={<span className="font-bold">₹</span>}
                data={institutionsData.map(inst => {
                  if (!inst.feeStructure) return 'N/A';
                  if (inst.feeStructure.mode === 'pdf') {
                    return (
                      <a
                        href={inst.feeStructure.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" />
                        View PDF
                      </a>
                    );
                  }
                  if (inst.feeStructure.entries && inst.feeStructure.entries.length > 0) {
                    return `${inst.feeStructure.entries.length} fee entries`;
                  }
                  return 'N/A';
                })}
              />

              {/* Results & Achievements Section */}
              <tr className="bg-gray-100">
                <td colSpan={institutionsData.length + 1} className="border border-gray-300 p-3">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <Trophy className="w-5 h-5" />
                    Results & Achievements
                  </h3>
                </td>
              </tr>

              <ComparisonRow
                label="Results & Achievements"
                icon={<Trophy className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  if (!inst.results || inst.results.length === 0) return 'N/A';
                  return (
                    <div className="space-y-2">
                      {inst.results.slice(0, 3).map((result, idx) => (
                        <div key={idx} className="text-sm bg-yellow-50 p-2 rounded">
                          <p className="font-semibold text-gray-900">{result.exam} - {result.year}</p>
                          {result.qualified && <p className="text-xs text-gray-600">Qualified: {result.qualified}</p>}
                          {result.topRank && <p className="text-xs text-gray-600">Top Rank: {result.topRank}</p>}
                        </div>
                      ))}
                      {inst.results.length > 3 && (
                        <p className="text-xs text-gray-500 italic">+{inst.results.length - 3} more results</p>
                      )}
                    </div>
                  );
                })}
              />

              {/* Digital Presence Section */}
              <tr className="bg-gray-100">
                <td colSpan={institutionsData.length + 1} className="border border-gray-300 p-3">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Digital Presence
                  </h3>
                </td>
              </tr>

              <ComparisonRow
                label="Social Media"
                icon={<Globe className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  const social = inst.socialMedia;
                  if (!social || (!social.facebook && !social.instagram && !social.twitter)) return 'N/A';
                  return (
                    <div className="flex gap-2">
                      {social.facebook && (
                        <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          <Facebook className="w-5 h-5" />
                        </a>
                      )}
                      {social.instagram && (
                        <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-800">
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      {social.twitter && (
                        <a href={social.twitter} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:text-sky-700">
                          <Twitter className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  );
                })}
              />

              {/* Virtual Tour */}
              <ComparisonRow
                label="Virtual Tour"
                icon={<Globe className="w-4 h-4" />}
                data={institutionsData.map(inst => {
                  if (!inst.virtualTourVideo) return 'N/A';
                  return (
                    <a
                      href={inst.virtualTourVideo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      Watch Tour
                    </a>
                  );
                })}
              />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ComparisonRow = ({ label, icon, data, highlightBest = false, bestIndex = -1 }) => {
  return (
    <tr className="hover:bg-gray-50">
      <td className="sticky left-0 bg-gray-100 border border-gray-300 p-4 font-semibold text-gray-900 min-w-[250px] w-[250px] z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm">{label}</span>
        </div>
      </td>
      {data.map((value, idx) => (
        <td 
          key={idx} 
          className={`border border-gray-300 p-4 text-sm align-top ${
            highlightBest && idx === bestIndex 
              ? 'bg-green-50 font-bold text-green-700' 
              : 'text-gray-700'
          }`}
        >
          {typeof value === 'string' ? (
            <span className={value === 'N/A' ? 'text-gray-400 italic' : ''}>
              {value}
              {highlightBest && idx === bestIndex && value !== 'N/A' && (
                <span className="ml-2 text-green-600">✓</span>
              )}
            </span>
          ) : (
            value
          )}
        </td>
      ))}
    </tr>
  );
};

export default ComparisonView;
